import { objectKeys } from "@appsmith/utils";
import type { JSAction } from "entities/JSCollection";
import store from "store";
import { getJSCollections } from "ee/selectors/entitiesSelector";
import { UNSTABLE_executeDynamicTrigger } from "workers/Evaluation/asyncWorkerActions";
import type { ToolCall } from "../component/types/toolCall";
import log from "loglevel";

/**
 * Service for executing tool calls
 */
export class ToolCallService {
  /**
   * Execute a tool call
   *
   * @param toolCall The tool call to execute
   * @param signal Abort signal for cancellation
   * @returns Promise resolving to the execution result
   */
  async executeToolCall(
    toolCall: ToolCall,
    signal?: AbortSignal,
  ): Promise<{
    status: "success" | "error" | "cancelled";
    result?: unknown;
    error?: string;
  }> {
    if (signal?.aborted) {
      return { status: "cancelled" };
    }

    try {
      let trigger: string;

      switch (toolCall.entity.type) {
        case "Query":
          trigger = `{{ (async () => {
            try {
              const result = await ${toolCall.entity.name}.run(${toolCall.entity.arguments});
              return result;
            } catch (error) {
              throw new Error(${toolCall.entity.name}.data);
            }
          })() }}`;
          break;
        case "JSFunction": {
          const functionArguments = JSON.parse(toolCall.entity.arguments);
          const jsFunction = this.getJSFunctionById(toolCall.entity.id);
          const jsArguments = jsFunction.actionConfiguration.jsArguments || [];

          if (jsArguments.length !== objectKeys(functionArguments).length) {
            throw new Error("Not all arguments are provided");
          }

          const isKeysMatch = objectKeys(functionArguments).every(
            (key) => jsArguments.find((arg) => arg.name === key) !== undefined,
          );

          if (!isKeysMatch) {
            throw new Error("Arguments keys do not match");
          }

          const argsArray = jsArguments.map(
            ({ name }) => functionArguments[name],
          );

          trigger = `{{${toolCall.entity.name}.apply(null, ${JSON.stringify(argsArray)})}}`;
          break;
        }
        default:
          throw new Error("Unsupported entity type");
      }

      // Check again for abort
      if (signal?.aborted) {
        return { status: "cancelled" };
      }

      log.debug("[FC]: trigger to execute", trigger);

      // Execute the trigger
      const response = await UNSTABLE_executeDynamicTrigger(trigger);

      log.debug("[FC]: execution response", response);

      if (response.errors.length > 0) {
        const errorMessage =
          response.errors[0]?.errorMessage?.message || "Unknown error";

        return { status: "error", error: errorMessage };
      }

      return { status: "success", result: response.result };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";

      return { status: "error", error: errorMessage };
    }
  }

  /**
   * Find a JS function by ID from the store
   *
   * @param jsFunctionId The ID of the JS function to find
   * @returns The JS function object
   * @throws Error if the function is not found
   */
  private getJSFunctionById(jsFunctionId: string): JSAction {
    const state = store.getState();
    const jsCollection = getJSCollections(state).find((jsCollection) =>
      jsCollection.config.actions.find(
        (jsFunction) => jsFunction.id === jsFunctionId,
      ),
    );

    if (!jsCollection) {
      throw new Error("JSFunction not found");
    }

    const jsFunction = jsCollection.config.actions.find(
      (jsFunction) => jsFunction.id === jsFunctionId,
    );

    if (!jsFunction) {
      throw new Error("JSFunction not found");
    }

    return jsFunction;
  }
}

export const toolCallService = new ToolCallService();
